// EligibilityRequirements.js
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, FileText, Briefcase, DollarSign, Languages, GraduationCap } from 'lucide-react';

const EligibilityRequirements = () => {
  const requirements = [
    {
      icon: Briefcase,
      title: 'Work Experience',
      description: 'Expect to show 1-2 years of skilled work in your field, with the exact figure set by the programme. That work has to sit inside NOC TEER 0, 1, 2 or 3.',
      applicable: 'Express Entry, PNPs'
    },
    {
      icon: Languages,
      title: 'Language Proficiency',
      description: 'Express Entry sets a floor of CLB 7, which is roughly IELTS 6.0. Results have to be current and come from IELTS, CELPIP or TEF. French adds points on top.',
      applicable: 'All Programs'
    },
    {
      icon: GraduationCap,
      title: 'Education',
      description: 'A Canadian high school diploma, or an equivalent from abroad, is the floor. Anything earned outside Canada needs an Educational Credential Assessment (ECA).',
      applicable: 'Express Entry, Most PNPs'
    },
    {
      icon: DollarSign,
      title: 'Proof of Funds',
      description: 'A single applicant has to show CAD $13,757, and that figure climbs with each additional family member. Anyone with a valid job offer or Canadian work experience is exempt.',
      applicable: 'Express Entry (FSWP), Some PNPs'
    },
    {
      icon: FileText,
      title: 'Valid Documents',
      description: 'Passport, police certificates, medical examination results and every piece of supporting evidence. Anything not already in English or French has to be translated.',
      applicable: 'All Programs'
    },
    {
      icon: CheckCircle2,
      title: 'Admissibility',
      description: 'You need to be in good health, free of a criminal record and able to support yourself once you arrive. Security, medical and financial grounds can each make an applicant inadmissible.',
      applicable: 'All Programs'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            Who Qualifies for Canadian PR
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Every programme sets its own bar, but almost all Canadian PR applications turn on the same handful of criteria.
          </p>
        </motion.div>

        {/* Requirements Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {requirements.map((requirement, index) => {
            const Icon = requirement.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-surface p-6 rounded-lg border border-gray-200 hover:border-secondary transition-colors"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-primary mb-1">
                      {requirement.title}
                    </h3>
                    <span className="text-xs text-secondary font-light">
                      {requirement.applicable}
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {requirement.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* CRS Score Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-gradient-to-r from-primary to-secondary rounded-lg p-8 text-white"
        >
          <h3 className="text-xl font-semibold mb-4">
            Comprehensive Ranking System (CRS) Score
          </h3>
          <p className="text-white/90 text-sm font-light leading-relaxed mb-4">
            Express Entry ranks candidates out of a maximum 1,200 points. Age, education, language ability and work history all feed into the total you are given.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Core Factors</p>
              <p className="text-white/80 font-light">600 points maximum</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Spouse or Partner</p>
              <p className="text-white/80 font-light">40 points maximum</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Additional Factors</p>
              <p className="text-white/80 font-light">600 points maximum</p>
            </div>
          </div>
        </motion.div>

        {/* Important Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 1 }}
          className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed">
            <strong>Please note:</strong> cut-off scores and eligibility rules shift as immigration targets and draw patterns change. We measure your profile against the rules in force at the time and advise on the route that suits your circumstances.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default EligibilityRequirements;
